/* eslint-disable sonarjs/cognitive-complexity */
import clsx from "clsx";
import NextLink from "next/link";
import { CSSProperties, RefObject } from "react";

import { DocumentType, Heading, Icon, Text } from "@afa-shared/afa-components";
import { TextList } from "@components/TextList";
import {
  getGapSize,
  getImageSize,
  getNotebookGapSize,
  getNotebookImageSize,
  getTabletGapSize,
  getTabletImageSize,
} from "@utils/imageHelpers/sharedImageHelper";

import cardStyles from "./Card.module.css";

export type ImageSize = "xs" | "s" | "m" | "l" | "xl";
export interface ICardProps {
  nextImage?: any;
  imgUrl?: string;
  imgSize?: ImageSize;
  imgAltTxt?: string;
  heading?: string;
  headingVariant?: "h2" | "h3" | "h4" | "h5" | "h6";
  text?: any;
  onCardClick?: any;
  reverse?: boolean;
  direction?: "row" | "column";
  linkText?: string;
  linkType?: "arrow_forward" | "open_in_new" | "file_download";
  linkUrl: string;
  linkVariant?: "link" | "button";
  className?: string;
  backgroundColor?: string;
  removeBorder?: boolean;
  removePadding?: boolean;
  fileExtension?: string;
  notebookSmallImage?: boolean;
  cardRef?: RefObject<any>;
  dataTestId?: string;
  isCampaign?: boolean;
  [htmlAttributes: string]: any;
}
export enum LinkType {
  External = "open_in_new",
  Internal = "arrow_forward",
  File = "file_download",
}

export enum LinkVariant {
  Link = "link",
  Button = "button",
}

type LinkProps = {
  href: string;
  children: React.ReactNode;
  isExternal?: boolean;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

export function Link({ href, children, isExternal = false, ...rest }: LinkProps) {
  if (isExternal) {
    return (
      <a href={href} {...rest}>
        {children}
      </a>
    );
  }

  return (
    <NextLink href={href} {...rest}>
      {children}
    </NextLink>
  );
}

const Card = ({
  nextImage,
  heading,
  headingVariant = "h2",
  text,
  textList,
  imgUrl,
  imgSize = "m",
  imgAltTxt,
  onCardClick,
  reverse,
  direction = "row",
  linkText,
  linkType = "arrow_forward",
  linkUrl,
  linkVariant = "link",
  backgroundColor,
  removeBorder = false,
  removePadding = false,
  notebookSmallImage = false,
  cardRef,
  className,
  fileExtension,
  dataTestId,
  isCampaign,
  ...htmlAttributes
}: ICardProps) => {
  const classnames = clsx(
    cardStyles.card,
    !reverse && direction === "row" && cardStyles.row,
    !reverse && direction === "column" && cardStyles.column,
    reverse && direction === "row" && cardStyles.rowReverse,
    reverse && direction === "column" && cardStyles.columnReverse,
    removeBorder && cardStyles.noBorder,
    removePadding && cardStyles.noPadding,
    (!linkText || linkText === "") && cardStyles.noLinkText,
    className
  );
  const iconName = linkType;

  const elementStyles: CSSProperties = {
    "--tablet-gap-size": getTabletGapSize(),
    "--tablet-image-size": getTabletImageSize(imgSize),
    "--notebook-gap-size": getNotebookGapSize(imgSize, notebookSmallImage),
    "--notebook-image-size": getNotebookImageSize(imgSize, notebookSmallImage),
    "--desktop-gap-size": getGapSize(imgSize),
    "--desktop-image-size": getImageSize(imgSize),
    backgroundColor,
  } as CSSProperties;

  const isButton = linkVariant === LinkVariant.Button;
  const isExternalLink = !isButton && linkType === LinkType.External;
  const isInternalLink =
    (!isButton && linkType === LinkType.Internal) || (!isButton && linkType === LinkType.File);

  return (
    <Link
      href={linkUrl}
      className={cardStyles.nonStyledLink}
      target={isExternalLink && "_self"}
      rel={isExternalLink && "noopener noreferrer"}
      onClick={onCardClick}
    >
      <article
        className={classnames}
        style={elementStyles}
        ref={cardRef}
        data-testid={dataTestId}
        {...htmlAttributes}
      >
        {nextImage ? (
          <div className={cardStyles.imageWrapper}>{nextImage}</div>
        ) : imgUrl ? (
          <div className={cardStyles.imageWrapper}>
            <img src={imgUrl} alt={imgAltTxt || ""} loading="lazy" />
          </div>
        ) : null}
        <div className={clsx(cardStyles.content, imgSize === "xl" && cardStyles.center)}>
          {isCampaign && <span className={cardStyles.campaignTag}>KAMPANJ</span>}
          <div className={cardStyles.textWrapper}>
            <>
              {fileExtension && (
                <DocumentType type={fileExtension} referenceSize={headingVariant}></DocumentType>
              )}
              <Heading variant={headingVariant} children={heading} />
            </>

            {text && <Text variant="paragraph" children={text} />}
            {textList && <TextList className={cardStyles.textList} blockData={textList} />}
          </div>

          {linkText && linkUrl && (
            <div className={cardStyles.linkWrapper} aria-hidden>
              <span
                className={clsx(
                  cardStyles.linkSpan,
                  linkVariant === "link" && cardStyles.link,
                  linkVariant === "button" && cardStyles.button,
                  isExternalLink && cardStyles.linkExternal,
                  isInternalLink && cardStyles.linkInternal
                )}
              >
                {isInternalLink && (
                  <Icon
                    name={iconName}
                    size="sm"
                    referenceSize="span"
                    className={cardStyles.linkIcon}
                  />
                )}
                <span>{linkText}</span>
                {isExternalLink && (
                  <Icon
                    name={iconName}
                    size="sm"
                    referenceSize="span"
                    className={cardStyles.linkIcon}
                  />
                )}
              </span>
            </div>
          )}
        </div>
      </article>
    </Link>
  );
};

export default Card;
